import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './CouponCelebration.css';

const BLAST_COLORS = ['#F5C21A', '#D4A416', '#16A34A', '#FB923C', '#FDE68A', '#FFFFFF', '#1F2428'];

type BlastPiece = {
  id: number;
  color: string;
  x: number;
  y: number;
  rot: number;
  delay: number;
  width: number;
  height: number;
};

function buildBlast(seed: string): BlastPiece[] {
  let n = 0;
  for (let i = 0; i < seed.length; i += 1) {
    n = (n * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const rand = () => {
    n = (n * 1664525 + 1013904223) >>> 0;
    return n / 4294967296;
  };
  return Array.from({ length: 56 }, (_, id) => {
    const angle = rand() * Math.PI * 2;
    const dist = 28 + rand() * 72;
    return {
      id,
      color: BLAST_COLORS[id % BLAST_COLORS.length],
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist - 8,
      rot: (rand() - 0.5) * 720,
      delay: rand() * 0.12,
      width: 6 + rand() * 8,
      height: 8 + rand() * 12,
    };
  });
}

export interface CouponCelebrationProps {
  open: boolean;
  code?: string;
  savingsLabel?: string | null;
  onClose: () => void;
}

const CouponCelebration: React.FC<CouponCelebrationProps> = ({
  open,
  code,
  savingsLabel,
  onClose,
}) => {
  const pieces = useMemo(() => buildBlast(code || 'coupon'), [code]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const timer = window.setTimeout(onClose, 5200);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(timer);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const content = (
    <div className="coupon-celeb-overlay" onClick={onClose} role="presentation">
      <div className="coupon-celeb-blast" aria-hidden>
        {pieces.map((piece) => (
          <span
            key={piece.id}
            className="coupon-celeb-piece"
            style={{
              background: piece.color,
              width: `${piece.width}px`,
              height: `${piece.height}px`,
              animationDelay: `${piece.delay}s`,
              ['--x' as '--x']: `${piece.x}vw`,
              ['--y' as '--y']: `${piece.y}vh`,
              ['--rot' as '--rot']: `${piece.rot}deg`,
            } as React.CSSProperties}
          />
        ))}
      </div>
      <div
        className="coupon-celeb-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="coupon-celeb-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="coupon-celeb-burst" aria-hidden />
        <div className="coupon-celeb-emoji" aria-hidden>
          🎉
        </div>
        <h2 id="coupon-celeb-title">Congratulations!</h2>
        <p className="coupon-celeb-copy">
          {code ? (
            <>
              Coupon <strong>{code}</strong> is applied on this order.
            </>
          ) : (
            'Your coupon is applied on this order.'
          )}
        </p>
        {savingsLabel ? <p className="coupon-celeb-save">You saved {savingsLabel}</p> : null}
        <button type="button" className="coupon-celeb-done" onClick={onClose}>
          Awesome
        </button>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return content;
  }
  return createPortal(content, document.body);
};

export default CouponCelebration;
